import { useState } from 'react';

/**
 * Sitzungsweiter Schalterzustand fuer "Werte der vorherigen Meldung anzeigen" (D-09).
 *
 * Dies ist der ERSTE `sessionStorage`-Zugriff des Projekts (24-RESEARCH.md § Frage 6, vollstaendiger
 * Repo-Grep ohne Treffer). D-09 legt zwei Dinge zugleich fest: GENAU EIN Schluessel fuer alle fuenf
 * Formulartypen (der Melder liest den Schalter als eine Einstellung, nicht als fuenf -- kein Typ,
 * keine Meldungs-ID im Schluessel), und bewusst NICHT der dauerhafte Web-Storage (die Einstellung
 * soll das Schliessen des Browsers NICHT ueberleben; das Projekt hat bisher keine dauerhafte
 * Bedienpraeferenz).
 *
 * Direkter, ungeschuetzter Zugriff ohne try/catch und ohne `window`-Guard -- exakt das Muster, das
 * `services/api.ts` fuer den dauerhaften Web-Storage vormacht (`clearAuthAndRedirect`/
 * `refreshAccessToken`, `:31-56`). Kein Wrapper-Modul: es gibt keinen Praezedenzfall im Bestand, und
 * die Einfachheit ist hier das Muster.
 */
export const PREVIOUS_VALUES_STORAGE_KEY = 'reports.showPreviousValues';

/**
 * Liest den gespeicherten Schalterzustand. Gibt genau dann `true` zurueck, wenn der gespeicherte
 * Wert woertlich die Zeichenkette `'true'` ist -- jeder andere Inhalt (fehlend, `'false'`, Muell aus
 * einer fremden Quelle) ergibt `false`. Bewusste Bauart: der Schalter startet im Zweifel aus, und
 * ein ausgeschalteter Schalter erzeugt keinen Netzaufruf (D-09).
 */
export function readPreviousValuesToggle(): boolean {
  return sessionStorage.getItem(PREVIOUS_VALUES_STORAGE_KEY) === 'true';
}

/** Schreibt den Schalterzustand unter dem einen gemeinsamen Schluessel. */
export function writePreviousValuesToggle(next: boolean): void {
  sessionStorage.setItem(PREVIOUS_VALUES_STORAGE_KEY, String(next));
}

/**
 * React-Hook um den Schalterzustand. `useState` mit `readPreviousValuesToggle` als Initialisierer
 * (Lazy-Form), damit der Speicherzugriff nur beim ersten Rendern laeuft, nicht bei jedem. Der Setter
 * schreibt erst ueber `writePreviousValuesToggle` und setzt danach den Zustand, damit
 * `sessionStorage` und React-State nie auseinanderlaufen.
 */
export function usePreviousValuesToggle(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(readPreviousValuesToggle);

  const setToggle = (next: boolean) => {
    writePreviousValuesToggle(next);
    setValue(next);
  };

  return [value, setToggle];
}
